import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Leaf, Download, Folder, ImageOff, ShieldCheck, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { devError } from '../utils/logger';

/**
 * /projects/shared/:token — public, read-only view of a buyer's project for
 * external NABERS assessors. No login required. The owner can revoke the
 * token from their project page at any time.
 */
export default function SharedProjectPage() {
  const { token } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects/shared/${token}`);
        if (!cancelled) setProject(data);
      } catch (e) {
        devError('shared project load failed', e);
        if (!cancelled) {
          setError(
            e.response?.status === 410
              ? 'This share link has expired. Ask the project owner to generate a new one.'
              : 'This share link is invalid or has been revoked.',
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const exportPack = async (template = 'nabers') => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/projects/shared/${token}/nabers-export`, {
        params: { template },
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${template}-pack.zip`;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success('Evidence pack downloaded');
    } catch (err) {
      devError('shared compliance export failed', err);
      toast.error('Could not generate export');
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen pt-24 pb-20" data-testid="shared-project-error">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h1 className="font-playfair text-3xl mb-3">Link unavailable</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="shared-project-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading shared project…
      </div>
    );
  }

  const totalKg = project.total_co2e_kg || 0;
  const totalLabel = totalKg >= 1000 ? `${(totalKg / 1000).toFixed(2)} t` : `${Math.round(totalKg)} kg`;

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="shared-project-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-accent mb-3">
          <ShieldCheck className="w-3 h-3" /> Shared for Green Star / Climate Active / NABERS assessment
        </div>
        <h1 className="font-playfair text-3xl md:text-4xl mb-1" data-testid="shared-project-name">
          {project.name}
        </h1>
        {project.description && (
          <p className="text-muted-foreground max-w-3xl">{project.description}</p>
        )}

        <div className="mt-8 grid gap-4 md:grid-cols-[1fr_auto] items-center rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5 md:p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Leaf className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Estimated CO₂e avoided</p>
              <p className="font-playfair text-3xl text-emerald-300" data-testid="shared-total-co2e">
                {totalLabel}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Across {project.listings?.length || 0} reclaimed item(s) · Bath ICE v3.0 methodology
              </p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={exporting}
                className="btn-gold"
                data-testid="shared-export-btn"
              >
                {exporting
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                Download evidence pack
                <ChevronDown className="w-3.5 h-3.5 ml-2 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Choose framework
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => exportPack('green-star-fitouts')}
                data-testid="shared-export-green-star-fitouts"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">Green Star Fitouts</span>
                  <span className="text-[11px] text-muted-foreground">GBCA · Design for Reuse + Responsible Procurement</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportPack('climate-active')}
                data-testid="shared-export-climate-active"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">Climate Active</span>
                  <span className="text-[11px] text-muted-foreground">Upfront Carbon for Buildings · A1–A5</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportPack('esp-construction')}
                data-testid="shared-export-esp-construction"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">DCCEEW ESP Policy</span>
                  <span className="text-[11px] text-muted-foreground">Commonwealth construction ≥ A$7.5M</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportPack('nabers')}
                data-testid="shared-export-nabers"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">NABERS</span>
                  <span className="text-[11px] text-muted-foreground">Embodied Emissions</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <h2 className="font-playfair text-xl mt-12 mb-4">Items in this project</h2>
        {(!project.listings || project.listings.length === 0) ? (
          <div className="bg-card border border-border/30 rounded-xl p-10 text-center text-muted-foreground" data-testid="shared-empty">
            <Folder className="w-10 h-10 text-accent/50 mx-auto mb-3" />
            No items shared yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {project.listings.map((l) => (
              <a
                key={l.listing_id}
                href={`/listing/${l.listing_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-xl overflow-hidden border border-border/30 bg-card"
                data-testid={`shared-listing-${l.listing_id}`}
              >
                <div
                  className="aspect-[4/3] bg-secondary/40 bg-cover bg-center flex items-center justify-center"
                  style={l.image_url ? { backgroundImage: `url(${l.image_url})` } : {}}
                >
                  {!l.image_url && <ImageOff className="w-8 h-8 text-muted-foreground/30" />}
                </div>
                <div className="p-3">
                  <p className="font-medium text-sm line-clamp-2 group-hover:text-accent transition-colors">{l.title}</p>
                  <p className="text-xs text-muted-foreground capitalize mt-1">{l.category}</p>
                  {l.carbon?.kg_co2e_total ? (
                    <p className="text-xs text-emerald-400 mt-2">
                      {Math.round(l.carbon.kg_co2e_total).toLocaleString()} kg CO₂e saved
                    </p>
                  ) : null}
                </div>
              </a>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-12 max-w-3xl">
          Figures are conservative estimates using cradle-to-gate factors from Bath ICE v3.0
          (Hammond &amp; Jones, 2019). Download the ZIP above for the full materials.csv,
          summary.pdf and transaction receipts needed for a NABERS Embodied Emissions submission.
        </p>
      </div>
    </div>
  );
}
